<script lang="ts" setup>
import { DefaultButton } from '@hantecmarket/hantec-ui-widget'
import { useForm } from 'vee-validate'
import { onMounted, ref } from 'vue'
import * as yup from 'yup'

import getPreference from '@/services/Forms/getPreference'
import { useGlobalSeoMetaHandler } from '@/services/seoGlobalService'
import {
  fetchTemplateOneStore,
  useTemplateOneData,
  useSeoIndexFollowUnsubscribePreferences
} from '@/services/Trading/templateOneService'

interface Categories {
  New_Product_Launches: boolean
  Product_Recommendations: boolean
  Promotional_emails: boolean
  Operational_emails: boolean
  Market_Analysis_Dynamic_Emails: boolean
}

const defaultCategories = ref<Categories>({
  New_Product_Launches: false,
  Product_Recommendations: false,
  Promotional_emails: false,
  Operational_emails: false,
  Market_Analysis_Dynamic_Emails: false
})

function getUtmParameters(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const utmParams: Record<string, string> = {}

  params.forEach((value, key) => {
    if (key === 'email') {
      utmParams[key] = value
    }
    if (key === 'user_id') {
      utmParams[key] = value
    }
    if (key === 'cid') {
      utmParams[key] = value
    }
    if (key === 'app_key') {
      utmParams[key] = value
    }
  })

  return utmParams
}

function isExternallLink(params: Object) {
  if (Object.keys(params).length > 1) {
    return true
  }
  return false
}

const params = getUtmParameters()

if (isExternallLink(params)) {
  // get categories from MoE via the API
  getPreference
    .get({
      action: 'get',
      user_id: params.user_id,
      cid: params.cid,
      app_key: params.app_key
    })
    .then((response) => {
      defaultCategories.value = { ...defaultCategories.value, ...response.data.categories.moengage }
    })
    .catch((error) => {
      console.error('Error:', error)
    })
} else {
  // get categories from API defaults
  getPreference
    .get({
      action: 'get',
      email: params.email
    })
    .then((response) => {
      defaultCategories.value = { ...defaultCategories.value, ...response.data.categories.local }
    })
    .catch((error) => {
      console.error('Error:', error)
    })
}

const isSubmitting = ref(false)
const isFormSubmitted = ref(false)

const trySubmit = async () => {
  isFormSubmitted.value = true
}

const schema = yup.object({})

interface FormValues {
  email: string
}

const { handleSubmit } = useForm<FormValues>({
  validationSchema: schema
})

const onSubmit = handleSubmit((values: FormValues) => {
  getPreference
    .get({
      action: 'post',
      email: params.email,
      user_id: params.user_id,
      cid: params.cid,
      app_key: params.app_key,
      update_categories: { ...defaultCategories.value }
    })
    .then(() => {
      showMessage.value = true
    })
    .catch((error) => {
      console.error('Error:', error)
    })
})

interface Category {
  name: string
  key: keyof Categories
}

const showMessage = ref(false)
interface Fields {
  sub_title: string
  sub_description: string
  footer_text: string
  privacy_policy_label: string
  privacy_policy_pdf: string
  update_button: string
  update_success: string
  categories: Category[]
}

const fields = ref<Fields>()

onMounted(async () => {
  await fetchTemplateOneStore('preferences-of-communication', '')
  fields.value = useTemplateOneData().value?.fields
  useGlobalSeoMetaHandler(useSeoIndexFollowUnsubscribePreferences())
})
</script>

<template>
  <section class="flex justify-center w-full pt-20 pb-20 lg:pb-40 lg:pt-40">
    <div class="flex flex-col w-full px-2.5 md:px-30 items-center">
      <h2 class="text-center h3-bold md:h2-bold">{{ fields?.sub_title }}</h2>
      <p class="text-midGrey-100 h6-regular md:subtitle text-center pt-2.5">
        {{ fields?.sub_description }}
      </p>

      <div class="mt-10 md:mt-15 md:mb-20 max-w-[430px] justify-center">
        <form class="w-[300px]" @submit.prevent="onSubmit" novalidate>
          <div v-for="category in fields?.categories" :key="category.key">
            <label>
              <input type="checkbox" v-model="defaultCategories[category.key]" />
              {{ category.name }}
            </label>
          </div>

          <DefaultButton
            :disabled="isSubmitting"
            class="button bg-hantec-primary text-white block text-center px-8 py-4 rounded-1.5 sm:flex items-center md:inline-flex h-12.5 mt-10 md:mt-5 md:mb-0 w-full justify-center flex w-[300px]"
            :class="{ 'pointer-events-none bg-gray-600 disabled': isSubmitting }"
            type="submit"
            @click="trySubmit"
          >
            <div class="">{{ fields?.update_button }}</div>
          </DefaultButton>
        </form>

        <div v-if="showMessage" class="mt-2 mb-5 lg:mb-1 text-red-150 text-center w-[300px]">
          {{ fields?.update_success }}
        </div>
      </div>

      <div class="mt-5 butter-wysiwyg">
        {{ fields?.footer_text }}
        <a href="/privacy-policy.pdf" class="mt-5 butter-wysiwyg">
          {{ fields?.privacy_policy_label }}
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
